import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

async function userFromToken(accessToken?: string | null) {
  if (!accessToken) return null;
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin.auth.getUser(accessToken);
  return data.user ?? null;
}

async function requireAdmin(accessToken: string) {
  const user = await userFromToken(accessToken);
  if (!user) throw new Error("Não autenticado");
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await (supabaseAdmin as any)
    .from("user_roles")
    .select("role")
    .eq("user_id", user.id)
    .eq("role", "admin")
    .maybeSingle();
  if (!data) throw new Error("Sem permissão");
  return { user, admin: supabaseAdmin as any };
}

export const registerPushToken = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        token: z.string().min(20).max(4096),
        previousToken: z.string().min(20).max(4096).optional().nullable(),
        platform: z.string().max(40).optional(),
        accessToken: z.string().max(4096).optional().nullable(),
        geo: geoSchema.optional().nullable(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const user = await userFromToken(data.accessToken);
    const row: Record<string, unknown> = {
      token: data.token,
      platform: data.platform ?? null,
      revoked_at: null,
      last_seen_at: new Date().toISOString(),
      ...(await geoRow(data.geo)),
    };
    if (user) row.user_id = user.id;
    const { data: saved, error } = await (supabaseAdmin as any)
      .from("push_subscriptions")
      .upsert(row, { onConflict: "token" })
      .select("id")
      .single();
    if (error) throw new Error("Falha ao salvar inscrição");
    if (data.previousToken && data.previousToken !== data.token) {
      await (supabaseAdmin as any)
        .from("push_subscriptions")
        .update({ revoked_at: new Date().toISOString() })
        .eq("token", data.previousToken);
    }
    if (user) await addPushLeadToNewsletter(supabaseAdmin as any, user.id).catch((e) => console.error("push lead", e));
    return { id: saved.id as string };
  });

const geoSchema = z.object({
  country: z.string().trim().max(8).nullable().optional(),
  region: z.string().trim().max(80).nullable().optional(),
  city: z.string().trim().max(120).nullable().optional(),
  timezone: z.string().trim().max(64).nullable().optional(),
});

/** Localização vinda do proxy (/__geo); cai para o país do cabeçalho da Cloudflare. */
async function geoRow(geo?: z.infer<typeof geoSchema> | null) {
  const { getRequestHeader } = await import("@tanstack/react-start/server");
  const hdr = (n: string) => { try { return getRequestHeader(n) || null; } catch { return null; } };
  const country = geo?.country || hdr("cf-ipcountry");
  const out: Record<string, string> = {};
  if (country && country !== "XX" && country !== "T1") out.country = country.toUpperCase();
  const region = geo?.region || hdr("cf-region-code") || hdr("cf-region");
  const city = geo?.city || hdr("cf-ipcity");
  const tz = geo?.timezone || hdr("cf-timezone");
  if (region) out.region = region;
  if (city) out.city = city;
  if (tz) out.timezone = tz;
  return out;
}

/** Logado que ativou push e aceita e-mails entra na newsletter (nunca reinscreve quem saiu). */
async function addPushLeadToNewsletter(admin: any, userId: string) {
  const { data: p } = await admin.from("profiles").select("email, full_name, email_notifications_enabled").eq("id", userId).maybeSingle();
  if (!p?.email || p.email_notifications_enabled === false) return;
  const email = String(p.email).trim().toLowerCase();
  const { data: existing } = await admin.from("newsletter_subscribers").select("id").eq("email", email).maybeSingle();
  if (existing) return; // já inscrito ou cancelou: não altera
  await admin.from("newsletter_subscribers").insert({
    name: (p.full_name || email.split("@")[0]).slice(0, 80),
    email,
    source_type: "push",
    source_slug: null,
    consent_text: "Aceito receber ofertas e novidades por e-mail do Shopping do Bem. (ativado junto às notificações, com e-mails habilitados no perfil)",
    consent_at: new Date().toISOString(),
  });
}

/** Registra interesse do inscrito numa marca (visualização, cupom revelado, ir às compras). */
export const trackPushInterest = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({
      token: z.string().min(20).max(4096),
      brandId: z.string().uuid(),
      event: z.enum(["view", "reveal", "shop_click"]),
      geo: geoSchema.optional().nullable(),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const admin = supabaseAdmin as any;
    const { data: sub } = await admin.from("push_subscriptions").select("id").eq("token", data.token).is("revoked_at", null).maybeSingle();
    if (!sub) return { ok: false };
    if (data.event === "view") {
      const since = new Date(Date.now() - 6 * 3600_000).toISOString();
      const { count } = await admin.from("push_interest_events").select("id", { count: "exact", head: true })
        .eq("subscription_id", sub.id).eq("brand_id", data.brandId).eq("event", "view").gte("created_at", since);
      if ((count ?? 0) > 0) return { ok: true };
    }
    const { data: b } = await admin.from("brands").select("category_id").eq("id", data.brandId).maybeSingle();
    await Promise.all([
      admin.from("push_interest_events").insert({ subscription_id: sub.id, brand_id: data.brandId, category_id: b?.category_id ?? null, event: data.event }),
      admin.from("push_subscriptions").update({ last_seen_at: new Date().toISOString(), ...(await geoRow(data.geo)) }).eq("id", sub.id),
    ]);
    return { ok: true };
  });

export const togglePushBrandFollow = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ token: z.string().min(20).max(4096), brandId: z.string().uuid(), follow: z.boolean() }).parse(d),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const admin = supabaseAdmin as any;
    const { data: sub } = await admin.from("push_subscriptions").select("id").eq("token", data.token).maybeSingle();
    if (!sub) throw new Error("Inscrição não encontrada");
    if (data.follow) {
      await admin
        .from("push_brand_follows")
        .upsert({ subscription_id: sub.id, brand_id: data.brandId }, { onConflict: "subscription_id,brand_id" });
    } else {
      await admin.from("push_brand_follows").delete().eq("subscription_id", sub.id).eq("brand_id", data.brandId);
    }
    return { ok: true };
  });

export const getPushFollowState = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ token: z.string().min(20).max(4096), brandId: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const admin = supabaseAdmin as any;
    const { data: sub } = await admin.from("push_subscriptions").select("id").eq("token", data.token).maybeSingle();
    if (!sub) return { following: false };
    const { data: f } = await admin
      .from("push_brand_follows")
      .select("id")
      .eq("subscription_id", sub.id)
      .eq("brand_id", data.brandId)
      .maybeSingle();
    return { following: !!f };
  });

const actionSchema = z.object({ title: z.string().trim().min(1).max(30), link: z.string().trim().min(1).max(500) });
const campaignSchema = z.object({
  accessToken: z.string().min(10),
  id: z.string().uuid().optional().nullable(),
  title: z.string().trim().min(3).max(65),
  body: z.string().trim().min(3).max(180),
  link: z.string().trim().max(500).optional().nullable(),
  image: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  audience: z.enum(["all", "logged", "visitors", "brand", "category"]),
  brandIds: z.array(z.string().uuid()).max(50).optional(),
  brandLists: z.array(z.enum(["followers", "interested", "shop_click", "buyers"])).max(4).optional(),
  categoryIds: z.array(z.string().uuid()).max(50).optional(),
  engagement: z.array(z.enum(["cold", "warm", "hot"])).max(3).optional(),
  countries: z.array(z.string().trim().max(8)).max(50).optional(),
  regions: z.array(z.string().trim().max(80)).max(50).optional(),
  cities: z.array(z.string().trim().max(120)).max(100).optional(),
  platforms: z.array(z.enum(["android", "windows", "ios", "mac", "linux"])).max(5).optional(),
  actions: z.array(actionSchema).max(2).optional(),
  ttlHours: z.number().int().min(1).max(672).optional(),
  utmCampaign: z.string().trim().max(60).optional().nullable(),
  mode: z.enum(["draft", "schedule", "now", "test"]),
  scheduledAt: z.string().datetime().optional().nullable(),
  currentToken: z.string().min(20).max(4096).optional().nullable(),
});

const AMAZON_BANNED = /doa[cç][aã]o|doar|ajuda social|solid[aá]ri|ong\b|causa/i;

export const saveOrSendPushCampaign = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => campaignSchema.parse(d))
  .handler(async ({ data }) => {
    const { user, admin } = await requireAdmin(data.accessToken);
    const srv = await import("./push.server");

    if (data.audience === "brand" && data.brandIds?.length) {
      const { data: bs } = await admin.from("brands").select("name").in("id", data.brandIds);
      const hasAmazon = (bs ?? []).some((b: any) => /amazon/i.test(b.name));
      const text = [data.title, data.body, ...(data.actions ?? []).map((a) => a.title)].join(" ");
      if (hasAmazon && AMAZON_BANNED.test(text)) throw new Error("Campanhas da Amazon não podem mencionar doação ou causas.");
    }

    if (data.mode === "test") {
      let rows: { id: string; token: string }[] = [];
      if (data.currentToken) {
        const { data: cur } = await admin
          .from("push_subscriptions").select("id, token").eq("token", data.currentToken).is("revoked_at", null).maybeSingle();
        if (cur) rows = [cur];
      }
      if (!rows.length) rows = await srv.activeTokens(admin, { userId: user.id });
      const r = await srv.sendPushToTokens(admin, rows, {
        title: data.title, body: data.body, link: data.link || "/", image: data.image || null,
        actions: data.actions, ttlHours: data.ttlHours,
      });
      return { total: rows.length, ...r, status: "test" as const, id: null };
    }

    if (data.mode === "schedule") {
      if (!data.scheduledAt || new Date(data.scheduledAt).getTime() < Date.now() + 60_000)
        throw new Error("Escolha uma data futura para o agendamento");
    }

    const row = {
      kind: "manual",
      title: data.title,
      body: data.body,
      link_url: data.link || "/",
      image_url: data.image || null,
      audience: data.audience,
      brand_id: data.brandIds?.[0] ?? null,
      audience_filter: {
        brandIds: data.brandIds ?? [], platforms: data.platforms ?? [], brandLists: data.brandLists ?? [],
        categoryIds: data.categoryIds ?? [], engagement: data.engagement ?? [],
        countries: data.countries ?? [], regions: data.regions ?? [], cities: data.cities ?? [],
      },
      actions: data.actions ?? [],
      ttl_hours: data.ttlHours ?? 24,
      utm_campaign: data.utmCampaign || null,
      status: data.mode === "draft" ? "draft" : data.mode === "schedule" ? "scheduled" : "draft",
      scheduled_at: data.mode === "schedule" ? data.scheduledAt : null,
      updated_at: new Date().toISOString(),
    };
    let id = data.id ?? null;
    if (id) {
      const { error } = await admin.from("push_campaigns").update(row).eq("id", id).in("status", ["draft", "scheduled"]);
      if (error) throw new Error("Falha ao salvar campanha");
    } else {
      const { data: ins, error } = await admin
        .from("push_campaigns").insert({ ...row, created_by: user.id, total: 0, sent: 0, failed: 0 }).select("id").single();
      if (error) throw new Error("Falha ao salvar campanha");
      id = ins.id;
    }
    if (data.mode === "now") {
      const l = await srv.getPushLimits(admin);
      if (srv.isQuietHour(l)) throw new Error(`Horário silencioso (${l.qs}h–${l.qe}h). Agende a campanha ou ajuste nas Configurações.`);
    }
    if (data.mode !== "now") return { id, status: row.status, total: 0, sent: 0, failed: 0, lastError: "" };
    const r = await srv.executeCampaign(admin, id!);
    return { id, status: "sent", ...(r ?? { total: 0, sent: 0, failed: 0, lastError: "" }) };
  });

export const estimatePushAudience = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    campaignSchema.pick({
      accessToken: true, audience: true, brandIds: true, platforms: true, brandLists: true,
      categoryIds: true, engagement: true, countries: true, regions: true, cities: true,
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const { admin } = await requireAdmin(data.accessToken);
    const { resolveAudience, underDailyLimit, getPushLimits } = await import("./push.server");
    const rows = await resolveAudience(admin, data);
    const l = await getPushLimits(admin);
    return { count: rows.length, available: underDailyLimit(rows, l.daily).length };
  });

/** Opções de localização existentes entre os inscritos ativos. */
export const getPushGeoOptions = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ accessToken: z.string().min(10) }).parse(d))
  .handler(async ({ data }) => {
    const { admin } = await requireAdmin(data.accessToken);
    const { data: rows } = await admin.from("push_subscriptions").select("country, region, city").is("revoked_at", null).limit(50000);
    const tally = (k: "country" | "region" | "city") => {
      const m = new Map<string, number>();
      for (const r of (rows ?? []) as any[]) if (r[k]) m.set(r[k], (m.get(r[k]) ?? 0) + 1);
      return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, count }));
    };
    return { countries: tally("country"), regions: tally("region"), cities: tally("city").slice(0, 300) };
  });

export const pushCampaignAction = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ accessToken: z.string().min(10), id: z.string().uuid(), action: z.enum(["cancel", "duplicate", "delete"]) }).parse(d),
  )
  .handler(async ({ data }) => {
    const { user, admin } = await requireAdmin(data.accessToken);
    if (data.action === "cancel") {
      await admin.from("push_campaigns").update({ status: "cancelled", updated_at: new Date().toISOString() }).eq("id", data.id).eq("status", "scheduled");
      return { ok: true, id: data.id };
    }
    if (data.action === "delete") {
      await admin.from("push_campaigns").delete().eq("id", data.id).in("status", ["draft", "cancelled"]);
      return { ok: true, id: data.id };
    }
    const { data: c } = await admin.from("push_campaigns").select("*").eq("id", data.id).maybeSingle();
    if (!c) throw new Error("Campanha não encontrada");
    const { data: ins } = await admin.from("push_campaigns").insert({
      kind: "manual", title: c.title, body: c.body, link_url: c.link_url, image_url: c.image_url, audience: c.audience,
      brand_id: c.brand_id, audience_filter: c.audience_filter, actions: c.actions, ttl_hours: c.ttl_hours,
      utm_campaign: c.utm_campaign, status: "draft", total: 0, sent: 0, failed: 0, created_by: user.id,
    }).select("id").single();
    return { ok: true, id: ins?.id as string };
  });

export const getPushAdminData = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ accessToken: z.string().min(10) }).parse(d))
  .handler(async ({ data }) => {
    const { admin } = await requireAdmin(data.accessToken);
    const since30 = new Date(Date.now() - 30 * 86400_000).toISOString();
    const [subsRes, { count: follows }, { data: campaigns }, { data: cfg }, { data: events }] = await Promise.all([
      admin.from("push_subscriptions").select("id, platform, user_id, created_at, last_seen_at, revoked_at, country, region").limit(20000),
      admin.from("push_brand_follows").select("id", { count: "exact", head: true }),
      admin.from("push_campaigns").select("*, brands(name)").order("created_at", { ascending: false }).limit(200),
      admin.from("_app_config").select("key, value").in("key", [
        "push_auto_coupon_enabled", "push_auto_donation_enabled", "push_daily_limit", "push_quiet_start", "push_quiet_end",
      ]),
      admin.from("push_events").select("created_at, campaign_id").gte("created_at", since30).limit(20000),
    ]);
    const subs = (subsRes.data ?? []) as any[];
    const active = subs.filter((s) => !s.revoked_at);
    const now = Date.now();
    const newIn = (days: number) => active.filter((s) => now - new Date(s.created_at).getTime() < days * 86400_000).length;
    const plat = (p: string | null) => {
      const x = (p ?? "").toLowerCase();
      if (x.includes("android")) return "Android";
      if (x.includes("ios") || x.includes("iphone")) return "iPhone";
      if (x.includes("windows")) return "Windows";
      if (x.includes("mac")) return "Mac";
      if (x.includes("linux")) return "Linux";
      return "Outro";
    };
    const byPlatform: Record<string, number> = {};
    for (const s of active) byPlatform[plat(s.platform)] = (byPlatform[plat(s.platform)] ?? 0) + 1;
    const byRegionMap: Record<string, number> = {};
    for (const s of active) { const k = s.region ? `${s.region}${s.country && s.country !== "BR" ? ` (${s.country})` : ""}` : (s.country ? `Sem estado (${s.country})` : "Não identificado"); byRegionMap[k] = (byRegionMap[k] ?? 0) + 1; }
    const byRegion = Object.entries(byRegionMap).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([name, value]) => ({ name, value }));
    const day = (iso: string) => new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Sao_Paulo" }).format(new Date(iso));
    const series: Record<string, { day: string; inscricoes: number; cliques: number }> = {};
    for (let i = 29; i >= 0; i--) {
      const d = day(new Date(now - i * 86400_000).toISOString());
      series[d] = { day: d.slice(5).split("-").reverse().join("/"), inscricoes: 0, cliques: 0 };
    }
    for (const s of subs) { const d = day(s.created_at); if (series[d]) series[d].inscricoes++; }
    for (const e of (events ?? []) as any[]) { const d = day(e.created_at); if (series[d]) series[d].cliques++; }
    const sentCampaigns = ((campaigns ?? []) as any[]).filter((c) => c.status === "sent");
    const accepted = sentCampaigns.reduce((a, c) => a + (c.sent ?? 0), 0);
    const clicks = sentCampaigns.reduce((a, c) => a + (c.clicks ?? 0), 0);
    const map = Object.fromEntries((cfg ?? []).map((r: any) => [r.key, r.value]));
    return {
      total: active.length,
      logged: active.filter((s) => s.user_id).length,
      revoked: subs.length - active.length,
      new7: newIn(7),
      new30: newIn(30),
      follows: follows ?? 0,
      accepted,
      clicks,
      ctr: accepted ? clicks / accepted : 0,
      byRegion,
      byPlatform: Object.entries(byPlatform).map(([name, value]) => ({ name, value })),
      series: Object.values(series),
      campaigns: (campaigns ?? []) as any[],
      autoCoupon: map.push_auto_coupon_enabled !== "false",
      autoDonation: map.push_auto_donation_enabled !== "false",
      dailyLimit: Number(map.push_daily_limit ?? 3),
      quietStart: Number(map.push_quiet_start ?? 22),
      quietEnd: Number(map.push_quiet_end ?? 8),
    };
  });

export const listPushSubscribers = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({
      accessToken: z.string().min(10),
      status: z.enum(["active", "revoked", "all"]).default("active"),
      kind: z.enum(["all", "logged", "visitors"]).default("all"),
      page: z.number().int().min(0).default(0),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const { admin } = await requireAdmin(data.accessToken);
    const size = 50;
    let q = admin
      .from("push_subscriptions")
      .select("id, platform, user_id, created_at, last_seen_at, revoked_at, country, region, city, push_brand_follows(count)", { count: "exact" })
      .order("last_seen_at", { ascending: false })
      .range(data.page * size, data.page * size + size - 1);
    if (data.status === "active") q = q.is("revoked_at", null);
    if (data.status === "revoked") q = q.not("revoked_at", "is", null);
    if (data.kind === "logged") q = q.not("user_id", "is", null);
    if (data.kind === "visitors") q = q.is("user_id", null);
    const { data: rows, count } = await q;
    const ids = [...new Set(((rows ?? []) as any[]).map((r) => r.user_id).filter(Boolean))];
    const { data: profs } = ids.length
      ? await admin.from("profiles").select("id, email, full_name").in("id", ids)
      : { data: [] };
    const pm = new Map(((profs ?? []) as any[]).map((p) => [p.id, p]));
    return {
      total: count ?? 0,
      pageSize: size,
      rows: ((rows ?? []) as any[]).map((r) => ({
        id: r.id as string,
        platform: (r.platform as string | null) ?? "desktop",
        user: r.user_id ? (pm.get(r.user_id)?.full_name || pm.get(r.user_id)?.email || "Usuário") : null,
        email: r.user_id ? (pm.get(r.user_id)?.email ?? null) : null,
        follows: r.push_brand_follows?.[0]?.count ?? 0,
        location: [r.city, r.region, r.country].filter(Boolean).join(", ") || null,
        createdAt: r.created_at as string,
        lastSeen: r.last_seen_at as string,
        revoked: !!r.revoked_at,
      })),
    };
  });

export const revokePushSubscription = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ accessToken: z.string().min(10), id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const { admin } = await requireAdmin(data.accessToken);
    await admin.from("push_subscriptions").update({ revoked_at: new Date().toISOString() }).eq("id", data.id);
    return { ok: true };
  });

export const setPushAutoSetting = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        accessToken: z.string().min(10),
        key: z.enum(["push_auto_coupon_enabled", "push_auto_donation_enabled", "push_daily_limit", "push_quiet_start", "push_quiet_end"]),
        enabled: z.boolean().optional(),
        value: z.number().int().min(0).max(24).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { admin } = await requireAdmin(data.accessToken);
    const value = data.value !== undefined ? String(data.value) : String(!!data.enabled);
    await admin.from("_app_config").upsert({ key: data.key, value }, { onConflict: "key" });
    return { ok: true };
  });
